<template>
  <Modal ref="modalRef" width="40rem" @after-close="pendingAction = null">
    <template #body>
      <View direction="column" gap="xl">
        <View direction="column" gap="sm">
          <Text variant="h2">Apply link · {{ address }}</Text>
          <Text v-if="view === 'create'" variant="body-large" color="secondary">
            Share it with anyone who wants to apply. They confirm their email with a code and the application opens
            right away, no tour needed. Their packet lands on your Applications page.
          </Text>
        </View>

        <!-- Marking it rented out turned its old links off; a link now is one more application, without relisting -->
        <Hint v-if="isRentedOut" icon="info-circle">
          This unit is marked rented out. Renters can still apply through this link.
        </Hint>

        <template v-if="view === 'ready' && myLink">
          <View direction="column" gap="sm">
            <Text variant="label-medium">Share this link with anyone who wants to apply</Text>
            <ApplyLinkUrlField :url="myLink.url" />
            <View gap="sm" align="center" wrap="wrap">
              <Badge variant="green" dot label="Active" size="small" />
              <Text variant="body-medium" color="secondary">{{ myStatusLine }}</Text>
            </View>
          </View>

          <WhatToExpectCard :items="whatRentersGet" />
        </template>

        <template v-if="view === 'renew' && myLink">
          <!-- A paused link still works: it takes the renter's details and the owner sends the application -->
          <ApplyLinkUrlField :url="myLink.url" variant="outline" />

          <Hint gap="md">
            <template #icon>
              <IconTile icon="pause-circle" size="small" color="orange" />
            </template>

            <View direction="column" gap="sm">
              <Text variant="h5">Your link is paused</Text>
              <Text variant="body-medium">
                Renters who open it are still added as leads, but each application waits for you to send it. Confirm
                below to make the link instant again. The URL stays the same.
              </Text>
            </View>
          </Hint>
        </template>

        <!-- The send step's own form, fed by the same requirements: only what is not on file is asked -->
        <ScreeningConfirmationsForm
          v-if="asksConfirmations && requirements"
          ref="confirmationsRef"
          :requirements="requirements"
        />

        <Hint v-if="view === 'create' && isRentPrepScreened" icon="info-circle">
          Your FCRA certification keeps the link instant for 90 days. After that the link pauses until you renew it
          here, and the URL stays the same.
        </Hint>

        <!-- After the renew form, which the paused hint points to. A paused link is renewed or turned off, not reset -->
        <template v-if="(view === 'ready' || view === 'renew') && myLink">
          <View v-if="!pendingAction" justify="space-between" align="center" gap="sm" wrap="wrap">
            <Text variant="body-small" color="tertiary">Created {{ formatDate(myLink.createdAt) }} by you</Text>
            <View gap="xs" wrap="wrap">
              <Button variant="ghost" appearance="danger" size="small" @click="askToConfirm('turn-off')">
                <Icon name="slash-circle-01" size="small" />
                <span>Turn off link</span>
              </Button>
              <Button
                v-if="view === 'ready'"
                variant="ghost"
                appearance="danger"
                size="small"
                @click="askToConfirm('reset')"
              >
                <Icon name="refresh" size="small" />
                <span>Reset link</span>
              </Button>
            </View>
          </View>

          <div v-else ref="confirmRef">
            <Hint icon="alert-triangle">
              <View direction="column" gap="md">
                <!-- For a link that leaked: the old URL answers "no longer active", the owner shares the new one -->
                <Text v-if="pendingAction === 'reset'" variant="body-medium">
                  Reset this link? The current URL stops working right away, and anyone who opens it is told it is no
                  longer active. You get a new link to share.
                </Text>
                <!-- Revoking gates only the token: the leads and held applications it brought in are untouched -->
                <Text v-else variant="body-medium">
                  Turn off this link? The URL stops working right away, and anyone who opens it is told it is no longer
                  active. Leads and applications that already came through it stay as they are, and you can get a new
                  link here any time.
                </Text>
                <View gap="sm" wrap="wrap">
                  <Button variant="outline" size="small" @click="pendingAction = null">Keep this link</Button>
                  <Button
                    v-if="pendingAction === 'reset'"
                    appearance="danger"
                    size="small"
                    :loading="isRevoking"
                    @click="resetLink"
                  >
                    Reset link
                  </Button>
                  <Button v-else appearance="danger" size="small" :loading="isRevoking" @click="turnOffLink">
                    Turn off link
                  </Button>
                </View>
              </View>
            </Hint>
          </div>
        </template>

        <View v-if="teamLinks.length" direction="column" gap="md">
          <View direction="column" gap="xs">
            <Text variant="h4">{{ view === 'view-only' ? "Your team's links" : "Teammates' links" }}</Text>
            <Text variant="body-medium" color="secondary">
              A link sends as the teammate who made it, under their own screening confirmations. Only they can renew,
              reset or turn it off.
            </Text>
          </View>

          <View v-for="link in teamLinks" :key="link.id" direction="column" gap="sm" class="team-link">
            <View gap="sm" align="center" wrap="wrap">
              <Text variant="label-medium">{{ link.isMine ? 'Your link' : link.sender.name }}</Text>
              <Badge v-bind="statusBadge(link)" dot size="small" />
              <Text variant="body-small" color="secondary">{{ teamLinkLine(link) }}</Text>
            </View>
            <ApplyLinkUrlField :url="link.url" variant="outline" />
          </View>
        </View>

        <!-- After the links: for a view-only seat copying one is the point, its own link the footnote -->
        <Hint v-if="view === 'view-only'" gap="md">
          <template #icon>
            <IconTile icon="lock-01" size="small" color="neutral" />
          </template>

          <View direction="column" gap="md" align="start">
            <Text variant="body-medium">{{ viewOnlyText }}</Text>
            <Button variant="outline" size="small" @click="askForAccess">Ask for access</Button>
          </View>
        </Hint>
      </View>
    </template>

    <template #footer>
      <View v-if="asksConfirmations" gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button :disabled="!canConfirm" :loading="isSaving" @click="confirm">{{ confirmLabel }}</Button>
      </View>

      <Button v-else @click="close">Done</Button>
    </template>
  </Modal>

  <ProvideInvestorAddressModal ref="addressModalRef" @address-saved="open" />
</template>

<script setup lang="ts">
import { format } from 'date-fns';
import { computed, nextTick, ref } from 'vue';

import { BackgroundRecordsProvider } from '@tallo/applications';
import {
  Badge,
  Button,
  Hint,
  Icon,
  IconTile,
  Modal,
  Text,
  View,
  WhatToExpectCard,
  WhatToExpectItem,
  serverErrorCode,
  useAlertsStore,
} from '@tallo/design-system';
import {
  applicationsService,
  applyLinkConfirmationRequiredErrorCode,
  ApplyLinkDto,
  applyLinkService,
  PropertyAttestationsDto,
  ScreeningConfirmationsForm,
  transUnionAddressRequiredErrorCode,
} from '@tallo/investor/applications';
import { usePropertyAccessDialogStore, usePropertyTeamQuery } from '@tallo/investor/property';

import ProvideInvestorAddressModal from '../provide-investor-address-modal/provide-investor-address-modal.vue';
import ApplyLinkUrlField from './apply-link-url-field.vue';

/**
 * The unit's apply links. A seat that works the unit gets one of its own, sending as them: the first visit asks
 * for whatever screening confirmation is not on file (the send step's form), then shows the link; a paused link
 * asks again to renew it. Every seat sees the team's links and may copy them; a view-only seat sees nothing else.
 */
const props = withDefaults(defineProps<{ propertyId: string; address: string; isRentedOut?: boolean }>(), {
  isRentedOut: false,
});

type ApplyLinkView = 'create' | 'ready' | 'renew' | 'view-only';
type OwnLinkAction = 'reset' | 'turn-off';

const whatRentersGet: WhatToExpectItem[] = [
  {
    icon: 'mail-01',
    title: 'Renters apply on the spot',
    description:
      'They confirm their email with a code and the application opens right away: no password, no tour needed.',
  },
  {
    icon: 'file-check-02',
    title: 'No charge to you',
    description: 'Each applicant pays their own screening fee. The packet lands on your Applications page.',
  },
  {
    icon: 'users-01',
    title: 'Applicants show up as leads',
    description: 'With "Apply Link" as the source and the application already sent to them.',
  },
];

const alertsStore = useAlertsStore();
const accessDialog = usePropertyAccessDialogStore();
const teamQuery = usePropertyTeamQuery(() => ({ kind: 'property', id: props.propertyId }));

const modalRef = ref<InstanceType<typeof Modal>>();
const addressModalRef = ref<InstanceType<typeof ProvideInvestorAddressModal>>();
const confirmationsRef = ref<InstanceType<typeof ScreeningConfirmationsForm>>();

const links = ref<ApplyLinkDto[]>([]);
const canManage = ref(false);
const requirements = ref<PropertyAttestationsDto | null>(null);
const isSaving = ref(false);
const pendingAction = ref<OwnLinkAction | null>(null);
const isRevoking = ref(false);
const confirmRef = ref<HTMLElement>();

const myLink = computed(() => links.value.find(({ isMine }) => isMine));

const view = computed<ApplyLinkView>(() => {
  if (!canManage.value) return 'view-only';
  if (!myLink.value) return 'create';

  return myLink.value.status === 'active' ? 'ready' : 'renew';
});

const asksConfirmations = computed(() => view.value === 'create' || view.value === 'renew');

// A view-only seat may still hold a link from when it worked the unit; it is listed with the team's
const teamLinks = computed(() =>
  view.value === 'view-only' ? links.value : links.value.filter(({ isMine }) => !isMine),
);

const isRentPrepScreened = computed(
  () => requirements.value?.backgroundRecordsProvider === BackgroundRecordsProvider.RENT_PREP,
);

const canConfirm = computed(() => confirmationsRef.value?.isComplete ?? false);

const confirmLabel = computed(() => {
  if (view.value === 'renew') return 'Renew link';

  return confirmationsRef.value?.asksAnything === false ? 'Get link' : 'Confirm and get link';
});

const formatDate = (value: string) => format(new Date(value), 'MMM d, yyyy');
const firstName = (name: string) => name.split(' ')[0];

const myStatusLine = computed(() =>
  myLink.value?.expiresAt
    ? `Sends as you · confirmations good until ${formatDate(myLink.value.expiresAt)}`
    : 'Sends as you',
);

const viewOnlyText = computed(() =>
  links.value.length
    ? 'You can copy and share these links. A link of your own needs manage access to this unit.'
    : 'No one on your team has an apply link for this unit yet. A link of your own needs manage access to this unit.',
);

function statusBadge(link: ApplyLinkDto): { label: string; variant: 'green' | 'orange' } {
  return link.status === 'active' ? { label: 'Active', variant: 'green' } : { label: 'Paused', variant: 'orange' };
}

function teamLinkLine(link: ApplyLinkDto): string {
  const sender = link.isMine ? 'you' : firstName(link.sender.name);

  if (link.blockers.some(({ item }) => item === 'sender-access')) {
    return `${link.isMine ? 'You' : sender} can no longer send from this unit, so applications wait for its manager`;
  }

  if (link.status === 'needs-confirmation') {
    return `applications wait for ${sender} to send them`;
  }

  return link.expiresAt
    ? `applications go out on their own until ${formatDate(link.expiresAt)}`
    : 'applications go out on their own';
}

/**
 * Loads the unit's links and, for a seat that works the unit and has no active link, what a link of its own still
 * needs. The requirements GET is a manage route (it registers the unit with TransUnion), so a view-only seat never
 * calls it.
 */
async function load(): Promise<void> {
  const [unitLinks, team] = await Promise.all([applyLinkService.list(props.propertyId), teamQuery.refresh()]);

  links.value = unitLinks;
  // As the manage-action guard reads it: only a definite "no" turns this into the view-only modal
  canManage.value = team.data?.canManage !== false;
  requirements.value = asksConfirmations.value
    ? await applicationsService.getPropertyAttestations(props.propertyId)
    : null;
}

async function open(): Promise<void> {
  try {
    await load();
    modalRef.value?.show();
  } catch (error) {
    // The company is set up at TransUnion once, by the first seat to open a send step, with that seat's address
    if (serverErrorCode(error) === transUnionAddressRequiredErrorCode) {
      addressModalRef.value?.modalRef?.show();
    } else {
      alertsStore.catchError(error);
    }
  }
}

/** Something on file changed since the modal opened: start over from what is on file now. */
async function reload(): Promise<void> {
  try {
    await load();
  } catch (error) {
    alertsStore.catchError(error);
  }
}

async function confirm(): Promise<void> {
  const isRenewal = view.value === 'renew';
  isSaving.value = true;

  try {
    // Only what the form showed: the backend reuses whatever is on file
    links.value = await applyLinkService.confirmAndCreate(
      props.propertyId,
      confirmationsRef.value?.toRequestBody() ?? {},
    );
    // Recorded, so the link is active; should it still read paused, the form shows what is left
    requirements.value = asksConfirmations.value
      ? await applicationsService.getPropertyAttestations(props.propertyId)
      : null;

    if (isRenewal && myLink.value?.status === 'active') {
      alertsStore.success('Your apply link is instant again');
    }
  } catch (error) {
    if (serverErrorCode(error) === applyLinkConfirmationRequiredErrorCode) {
      await reload();
    } else {
      alertsStore.catchError(error);
    }
  } finally {
    isSaving.value = false;
  }
}

/** The confirm sits under the expect card or the renew form, below the fold of a short screen: bring it into view. */
async function askToConfirm(action: OwnLinkAction): Promise<void> {
  pendingAction.value = action;
  await nextTick();
  confirmRef.value?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
}

async function resetLink(): Promise<void> {
  isRevoking.value = true;

  try {
    await applyLinkService.revokeOwn(props.propertyId);
    links.value = await applyLinkService.confirmAndCreate(props.propertyId, {});
    alertsStore.success('Your apply link was reset. The old one no longer works.');
  } catch (error) {
    // Once the old link is off, a confirmation that lapsed meanwhile is asked for before the new one
    if (serverErrorCode(error) !== applyLinkConfirmationRequiredErrorCode) {
      alertsStore.catchError(error);
    }

    await reload();
  } finally {
    isRevoking.value = false;
    pendingAction.value = null;
  }
}

/** No new link follows: the modal moves to the create view, a one-click "Get link" when everything is on file. */
async function turnOffLink(): Promise<void> {
  isRevoking.value = true;

  try {
    await applyLinkService.revokeOwn(props.propertyId);
    alertsStore.success('Your apply link is turned off');
  } catch (error) {
    // An admin or Mark as Rented may have turned it off meanwhile: the reload shows what is there now
    alertsStore.catchError(error);
  }

  // The create view needs the requirements, which the ready view never loaded
  await reload();
  isRevoking.value = false;
  pendingAction.value = null;
}

function askForAccess() {
  close();
  accessDialog.open(props.propertyId);
}

function close() {
  modalRef.value?.close();
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}

.team-link + .team-link {
  padding-top: var(--t-spacing-md);
  border-top: 1px solid var(--t-color-neutral-200);
}
</style>
